import { Periodicity } from "@modules/completedSessions/completedSessionsType"
import { word } from "@modules/core/utils/i18n"
import { Language } from "@modules/language/utils/languageUtils"
import dayjs, { Dayjs } from "dayjs"
import dayjsDuration from "dayjs/plugin/duration"
dayjs.extend(dayjsDuration)

export const defaultDateFormat = "YYYY-MM-DD"
export const defaultDateTimeFormat = "YYYY-MM-DDTHH:mm:ssZ"
export const displayedDateFormat = "dddd DD MMM"
export const displayedFullDateFormat = "dddd, D MMMM YYYY"
export const monthYearDateFormat = "YYYY-MM"
export const weekdayDateFormat = (language: Language) =>
  language === Language.ENGLISH_US && navigator.language.toLowerCase() === "en-us" ? "ddd MM/DD" : "ddd DD/MM"

export const verboseDateFormat = (date: Dayjs, language: Language) =>
  language === Language.ENGLISH_US && navigator.language.toLowerCase() === "en-us"
    ? date.format("MMM DD")
    : date.format("DD MMM")

export const verboseDateIntervalFormat = (startDate: Dayjs, endDate: Dayjs, language: Language) =>
  startDate.isSame(endDate, "month")
    ? language === Language.ENGLISH_US && navigator.language.toLowerCase() === "en-us"
      ? `${startDate.format("MMM DD")} - ${endDate.format("DD")}`
      : `${startDate.format("DD")} - ${endDate.format("DD MMM")}`
    : language === Language.ENGLISH_US && navigator.language.toLowerCase() === "en-us"
      ? `${startDate.format("MMM DD")} - ${endDate.format("MMM DD")}`
      : `${startDate.format("DD MMM")} - ${endDate.format("DD MMM")}`

export const initialStartTime = "08:00"
export const initialEndTime = "09:00"

export const dateSeparator = "day-"

export const getMonthNumber = (date: Date) => {
  return date.getMonth() + 1
}

export const formatDateWithHyphen = (date: string) => {
  return date.replaceAll("/", "-")
}

export const formatDateWithHyphenUnbreakable = (date: string) => {
  return date.replaceAll("/", "\u2011")
}

export const getDuration = (timeA: Date | null, timeB: Date | null) => {
  const difference = Math.abs(dayjs(timeA).diff(dayjs(timeB)))
  return dayjs.duration(difference)
}

export const getDurationFromSecond = (durationInS: number) => {
  return dayjs.duration(durationInS, "s")
}

export const createDateTimeWithDateAndTime = (date: Date | null, dateTime: Date | null) => {
  return !!date && !!dateTime
    ? dayjs(date)
        .set("hours", dateTime.getHours())
        .set("minutes", dateTime.getMinutes())
        .set("milliseconds", dateTime.getMilliseconds())
        .toDate()
    : undefined
}

export const dateFormatByPeriodicity = ({
  beginDate,
  endDate,
  selectedPeriodicity,
  language,
}: {
  beginDate: Date
  endDate: Date
  selectedPeriodicity: Periodicity
  language: Language
}) => {
  if (selectedPeriodicity === Periodicity.WEEKLY) {
    if (language === Language.ENGLISH_US && navigator.language.toLowerCase() === "en-us") {
      const formattedBeginDate = dayjs(beginDate).format("MMM DD")

      const formattedEndDate = dayjs(endDate).format(beginDate.getMonth() === endDate.getMonth() ? "DD" : "MMM DD")

      return `${formattedBeginDate}-${formattedEndDate}`
    } else {
      const formattedBeginDate = dayjs(beginDate).format(beginDate.getMonth() === endDate.getMonth() ? "DD" : "DD MMM")

      const formattedEndDate = dayjs(endDate).format("DD MMM")

      return `${formattedBeginDate}-${formattedEndDate}`
    }
  } else if (selectedPeriodicity === Periodicity.MONTHLY) {
    const formattedBeginDate = dayjs(beginDate).format("MMM YY")

    return `${formattedBeginDate}`
  } else if (selectedPeriodicity === Periodicity.YEARLY) {
    const formattedBeginDate = dayjs(beginDate).format("YYYY")

    return `${formattedBeginDate}`
  } else {
    return ""
  }
}

export const useTimeCharAndMasks = () => {
  const dayChar = `000${word("global.day.label", { count: 1 })[0]} 00${word("global.hour.label", { count: 1 })[0]} 00${
    word("global.minute.label", { count: 1 })[0]
  }`
  const dayMask = dayChar.replaceAll("0", "9")
  const hourChar = `00${word("global.hour.label", { count: 1 })[0]} 00${word("global.minute.label", { count: 1 })[0]}`
  const hourMask = hourChar.replaceAll("0", "9")

  return {
    dayChar,
    dayMask,
    hourChar,
    hourMask,
  }
}

export const getMonthDates = (period: string) => {
  const date = dayjs(period, "YYYY-MM")

  return {
    firstDate: date.startOf("month").format("L"),
    lastDate: date.endOf("month").format("L"),
  }
}
